import React, { memo, useMemo, useState, useEffect } from "react";
import type { AsyncDuckDBConnection } from "@duckdb/duckdb-wasm";
import { Input } from "@ui/input";
import { Button } from "@ui/button";
import { ScrollArea } from "@ui/scroll-area";
import { X as CloseIcon } from "lucide-react";
import { useDebounce } from "@/lib";
import { useDistinctAutocomplete } from "../useDistinctAutocomplete";
import type { Filter, OpKey } from "../useFilters";
import { OPS } from "../useFilters";
import { isDuckDateType } from "../detect";

interface Props {
  row: Filter;
  allColumns: string[];
  duckTypes: Record<string, string>;
  conn: AsyncDuckDBConnection | null;
  table: string;
  update: (id: number, patch: Partial<Filter>) => void;
  remove: (id: number) => void;
}

export const FilterRow = memo(function FilterRow({
  row,
  allColumns,
  duckTypes,
  conn,
  table,
  update,
  remove,
}: Props) {
  /* column autocomplete */
  const [colOpen, setColOpen] = useState(false);
  const colMatches = useMemo(
    () =>
      allColumns
        .filter((c) =>
          c.toLowerCase().includes((row.column || "").toLowerCase()),
        )
        .slice(0, 50),
    [allColumns, row.column],
  );

  const pickColumn = (c: string) => {
    update(row.id, { column: c, value: "" });
    setColOpen(false);
  };

  /* value input (single string kept in row.value) */
  const [valDraft, setValDraft] = useState(row.value);
  const [valOpen, setValOpen] = useState(false);
  const debVal = useDebounce(valDraft, 150);

  /* autocomplete for non‑date columns only */
  const columnForSuggestions =
    allColumns.includes(row.column) && !isDuckDateType(duckTypes[row.column])
      ? row.column
      : "";

  const { values: suggestions } = useDistinctAutocomplete(
    conn,
    table,
    columnForSuggestions,
    debVal,
    { contains: true },
  );

  /* sync external change */
  useEffect(() => {
    setValDraft(row.value);
  }, [row.value]);

  /* helper – split/join the BETWEEN value */
  const splitBetween = (v: string): [string, string] => {
    const [lo = "", hi = ""] = v.split(/\.{2,}|,/).map((s) => s.trim());
    return [lo, hi];
  };

  /* column’s DuckDB type */
  const duckType = duckTypes[row.column] || "";

  /* dynamic value editor */
  const renderValueInput = () => {
    /* BETWEEN gets two inputs */
    if (row.op === "between") {
      const [lo, hi] = splitBetween(valDraft);

      const emit = (loVal: string, hiVal: string) => {
        const joined = `${loVal.trim()}..${hiVal.trim()}`;
        setValDraft(joined);
        update(row.id, { value: joined });
      };

      // pick input type depending on column
      const typeAttr = isDuckDateType(duckType)
        ? /timestamp/i.test(duckType)
          ? "datetime-local"
          : "date"
        : undefined;

      return (
        <div className="flex gap-1">
          <Input
            type={typeAttr}
            placeholder="From"
            className="flex-1"
            value={lo}
            onChange={(e) => emit(e.target.value, hi)}
          />
          <Input
            type={typeAttr}
            placeholder="To"
            className="flex-1"
            value={hi}
            onChange={(e) => emit(lo, e.target.value)}
          />
        </div>
      );
    }

    /* DATE / TIMESTAMP (single‑value) */
    if (isDuckDateType(duckType)) {
      if (/timestamp/i.test(duckType)) {
        return (
          <input
            type="datetime-local"
            className="w-full h-9 border rounded-md px-2 bg-background"
            value={valDraft}
            onChange={(e) => {
              setValDraft(e.target.value);
              update(row.id, { value: e.target.value });
            }}
          />
        );
      }
      return (
        <input
          type="date"
          className="w-full h-9 border rounded-md px-2 bg-background"
          value={valDraft}
          onChange={(e) => {
            setValDraft(e.target.value);
            update(row.id, { value: e.target.value });
          }}
        />
      );
    }

    /* plain text input + suggestions */
    return (
      <>
        <Input
          placeholder="Value…"
          value={valDraft}
          onFocus={() => setValOpen(true)}
          onBlur={() => setTimeout(() => setValOpen(false), 150)}
          onChange={(e) => setValDraft(e.target.value)}
          onBlurCapture={() => update(row.id, { value: valDraft })}
          onKeyDown={(e) => {
            if (e.key === "Enter") update(row.id, { value: valDraft });
          }}
        />
        {valOpen && debVal && suggestions.length > 0 && (
          <ScrollArea className="absolute z-30 mt-1 w-full max-h-40 border rounded-md bg-background">
            {suggestions.map((v) => (
              <button
                key={v}
                className="block w-full text-left px-2 py-1 hover:bg-muted text-sm"
                onMouseDown={() => {
                  setValDraft(v);
                  update(row.id, { value: v });
                }}
              >
                {v}
              </button>
            ))}
          </ScrollArea>
        )}
      </>
    );
  };

  return (
    <div className="flex items-end gap-2 bg-background p-2 rounded">
      {/* column picker */}
      <div className="w-48 relative">
        <label className="text-xs">Column</label>
        <Input
          placeholder="Column"
          value={row.column}
          onFocus={() => setColOpen(true)}
          onBlur={() => setTimeout(() => setColOpen(false), 150)}
          onChange={(e) => update(row.id, { column: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") pickColumn(row.column);
          }}
        />

        {colOpen && row.column && colMatches.length > 0 && (
          <ScrollArea className="absolute z-30 mt-1 w-full max-h-40 border rounded-md bg-background">
            {colMatches.map((c) => (
              <button
                key={c}
                className="block w-full text-left px-2 py-1 hover:bg-muted text-sm"
                onMouseDown={() => pickColumn(c)}
              >
                {c}
              </button>
            ))}
          </ScrollArea>
        )}
      </div>

      {/* operator */}
      <div className="w-32">
        <label className="text-xs">Op</label>
        <select
          className="w-full h-9 border rounded-md px-2 bg-background"
          value={row.op}
          onChange={(e) => update(row.id, { op: e.target.value as OpKey })}
        >
          {OPS.map((o) => (
            <option key={o.sql} value={o.sql}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* value(s) */}
      <div className="flex-1 relative">
        <label className="text-xs">{row.op === "between" ? "From / To" : "Value"}</label>
        {renderValueInput()}
      </div>

      {/* remove */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => remove(row.id)}
        title="Remove filter"
      >
        <CloseIcon className="h-4 w-4" />
      </Button>
    </div>
  );
});